<template>
  <v-container style="max-width:100vw;">
    <v-card style="background:white;" class="elevation-0 pa-4">
      <v-card-title>
        Influencers
        <v-btn icon @click="openFilter = !openFilter">
          <v-icon>mdi-filter</v-icon>
        </v-btn>
        <v-menu offset-y v-if="hasPermission('downloadSales')">
          <template v-slot:activator="{ on, attrs }">
            <v-btn v-bind="attrs" v-on="on" icon>
              <v-icon>mdi-download</v-icon>
            </v-btn>
          </template>
          <v-list>
            <v-list-item @click="exportExcel(options.itemsPerPage)">
              <v-list-item-title>
                Exportar <span style="font-size:10px;">({{ options.itemsPerPage }})</span>
              </v-list-item-title>
            </v-list-item>
            <v-list-item @click="exportExcel(totalInfluencers)">
              <v-list-item-title>
                Exportar Todo <span style="font-size:10px;">({{ totalInfluencers }})</span>
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
        <v-spacer></v-spacer>
        <div class="ma-0">
          <v-row class="ma-0" style="min-width:40vw;">
            <v-text-field class="pt-0 pb-2" v-model="search" append-icon="mdi-magnify" label="Buscar" single-line hide-details />
          </v-row>
          <v-row class="ma-0 pb-0" v-if="openFilter">
            <v-col cols="4">
              <v-autocomplete v-model="searchbyCategory" small-chips :items="categories" item-value="id" item-text="name" label="Categoría(s)" multiple chips />
            </v-col>
            <v-col cols="3">
              <v-autocomplete v-model="searchbyState" :items="statesList" item-value="id" item-text="name" label="Estado" />
            </v-col>
            <v-col cols="3">
              <v-autocomplete v-model="searchbyCity" :disabled="!searchbyState" :items="citiesList" item-value="id" item-text="name" label="Ciudad" />
            </v-col>
            <v-col cols="1" class="d-flex align-end">
              <v-btn class="elevation-0 mt-4" color="primary" rounded small @click="getDataFromApi">Filtrar</v-btn>
            </v-col>
          </v-row>
        </div>
      </v-card-title>
      <v-data-table
        dense
        height="600"
        fixed-header
        :footer-props="{'items-per-page-options':[15, 30, 50, totalInfluencers]}"
        v-if="showTable"
        :headers="headers"
        :items="influencers"
        class="elevation-0 px-6 pb-4 pt-6"
        :options.sync="options"
        :server-items-length="totalInfluencers"
        :loading="loading"
      >
        <template v-slot:item="{ item }">
          <tr>
            <td v-for="header in headers" :key="header.value">
              <!-- ACCIONES -->
              <template v-if="header.value === 'actions'">
                <v-menu bottom left>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon v-bind="attrs" v-on="on">
                      <v-icon>mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>
                  <v-list style="font-size:13px;">
                    <v-list-item v-if="item.tax_regime" @click="downloadPDF(item.editedItem)">
                      <v-icon small class="mr-2 pt-1">mdi-download</v-icon>
                      Facturas {{ currentMonth }}
                    </v-list-item>
                    <v-list-item v-if="hasPermission('editInfluencers')" @click="edit(item.editedItem)">
                      <v-icon small class="mr-2">mdi-pencil</v-icon>
                      Editar
                    </v-list-item>
                    <v-list-item v-if="hasPermission('deleteInfluencers')" @click="deleteItem(item)">
                      <v-icon small class="mr-2">mdi-delete</v-icon>
                      Eliminar
                    </v-list-item>
                  </v-list>
                </v-menu>
              </template>

              <!-- CITY AUTOCOMPLETE -->
              <template v-else-if="editCell.id === item.id && editCell.field === header.value && header.value === 'city' && header.editable">
                <v-autocomplete
                  v-model="editCell.value"
                  :items="citiesListMap[item.id] || []"
                  item-text="name"
                  item-value="id"
                  dense outlined hide-details
                  autofocus
                  label="Ciudad"
                  @change="saveCell(item, header)"
                  @blur="saveCell(item, header)"
                />
              </template>

              <!-- AGENT AUTOCOMPLETE -->
              <template v-else-if="editCell.id === item.id && editCell.field === header.value && header.value === 'agent' && header.editable">
                <v-autocomplete
                  v-model="editCell.value"
                  :items="getTalentsAgentList(item)"
                  item-text="name"
                  item-value="id"
                  dense outlined hide-details
                  autofocus
                  label="Agente"
                  @change="saveCell(item, header)"
                  @blur="saveCell(item, header)"
                />
              </template>

              <!-- FECHA DE NACIMIENTO: DATE PICKER -->
              <template v-else-if="editCell.id === item.id && editCell.field === header.value && header.value === 'birth_date' && header.editable">
                <v-menu
                  ref="dateMenu"
                  v-model="datePickerMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="editCell.value"
                      label="Fecha de nacimiento"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      dense outlined hide-details
                      style="min-width:120px;"
                    />
                  </template>
                  <v-date-picker
                    v-model="datePicker"
                    locale="es"
                    @input="onDatePicked"
                    scrollable
                    :max="maxDate"
                  ></v-date-picker>
                </v-menu>
              </template>

              <!-- INSTAGRAM Y TIKTOK: INPUTS NORMALES -->
              <template v-else-if="editCell.id === item.id && editCell.field === header.value && header.value === 'instagram' && header.editable">
                <v-text-field
                  v-model="editCell.value"
                  label="Instagram"
                  dense outlined hide-details autofocus
                  @keydown.enter.native="saveCell(item, header)"
                  @blur="saveCell(item, header)"
                  style="min-width:100px;"
                />
              </template>
              <template v-else-if="editCell.id === item.id && editCell.field === header.value && header.value === 'tiktok' && header.editable">
                <v-text-field
                  v-model="editCell.value"
                  label="Tiktok"
                  dense outlined hide-details autofocus
                  @keydown.enter.native="saveCell(item, header)"
                  @blur="saveCell(item, header)"
                  style="min-width:100px;"
                />
              </template>

              <!-- CELDA EN MODO EDICIÓN GENERICA -->
              <template v-else-if="editCell.id === item.id && editCell.field === header.value && header.editable">
                <component
                  :is="getInputComponent(header.type)"
                  v-model="editCell.value"
                  :key="editCell.id + '-' + editCell.field + '-' + editCell.type"
                  dense
                  outlined
                  autofocus
                  hide-details
                  @keydown.enter.native="saveCell(item, header)"
                  @blur="header.type !== 'file' ? saveCell(item, header) : null"
                  @change="header.type === 'file' ? onFileChange($event, item, header) : null"
                  :accept="header.type === 'file' ? '.pdf,.doc,.docx,.jpg,.png' : undefined"
                  style="min-width:100px;"
                />
              </template>


              <!-- UNIVERSAL EDITABLE (MOSTRAR ARCHIVO O TEXTO) -->
                <template v-else-if="header.editable">
                    <span
                        class="editable-cell name-cell"
                        style="display: flex; align-items: center; position:relative;"
                    >
                        <template v-if="header.type === 'file' && item[header.value]">
                        <a v-bind:href="'https://unopack.mx/files/'+item[header.value]" target="_blank">
                            <v-icon small>mdi-file</v-icon>
                        </a>
                        </template>
                        <v-btn
                         v-else-if="header.value === 'name'"
                        class="btnText"
                        :to="{ path: '/influencers/influencer/' + item.id }"
                        style="flex: 1 1 auto;"
                        >
                        {{ item.name }}
                        </v-btn>
                        <template v-else-if="item[header.value]">
                        {{ formatCell(item[header.value], header.type) }}
                        </template>
                        <template v-else>
                            <div style="min-height: 40px;"></div>
                        </template>
                        <v-icon
                        @click="startEditCell(item, header)"
                        x-small
                        class="edit-icon ml-2"
                        >
                        mdi-pencil
                        </v-icon>
                    </span>
                    </template>


              <!-- SOLO VISTA -->
              <template v-else>
                {{ formatCell(item[header.value], header.type) }}
              </template>
            </td>
          </tr>
        </template>
      </v-data-table>

      <!-- MODALES -->
      <v-dialog v-model="editDialog" max-width="800px">
        <editItem @closeDialogItem="closeDialogItem" :editedItem="editedItem" />
      </v-dialog>
      <v-dialog v-model="createDialog" max-width="800px">
        <template v-slot:activator="{ on, attrs }" v-if="hasPermission('createInfluencers')">
          <v-btn bottom color="#58c6ef" dark fab fixed right v-bind="attrs" v-on="on">
            <v-icon color="white">mdi-plus</v-icon>
          </v-btn>
        </template>
        <createItem @closeDialogItem="closeDialogItem" />
      </v-dialog>
    </v-card>
    <v-snackbar :color="snackbar.color" v-model="snackbar.show">
      {{ snackbar.message }}
    </v-snackbar>
  </v-container>
</template>

<script>
import axios from "axios";
import editItem from "../influencers/edit";
import createItem from "../influencers/create";
import { VFileInput, VTextarea } from 'vuetify/lib';

const AUTH_HEADER = 'Bearer ' + localStorage.getItem("xn8(*&^%$#47b*g3f76gw3*&^fn734gf8q*&^&^&mwmeg934g8whmre7ngN&G#*!NG@*&#GF(!*#gm1n428fm1g3n84gmf*^$&(%$)e3298negmg");

export default {
  components: { editItem, createItem, VFileInput, VTextarea },
  data: () => ({
    talentsAgentList: [],
    citiesListMap: [],
    snackbar: { show: false, message: null, color: null },
    openFilter: false,
    options: {},
    showTable: true,
    influencers: [],
    totalInfluencers: 0,
    loading: false,
    citiesList: [],
    search: '',
    editDialog: false,
    createDialog: false,
    editedItem: '',
    editCell: { id: null, field: null, value: null, type: null },
    datePickerMenu: false,
    datePicker: null,
    maxDate: new Date().toISOString().substr(0, 10),
    headers: [
      { text: 'TA', value: 'agent', sortable: false, type: 'autocomplete', editable: true, items: 'talentsAgentList', itemText: 'name', itemValue: 'id' },
      { text: 'Nombre', value: 'name', sortable: false, type: 'text', editable: true },
      { text: 'Instagram', value: 'instagram', sortable: false, type: 'text', editable: true },
      { text: 'Tiktok', value: 'tiktok', sortable: false, type: 'text', editable: true },
      { text: '#Followers', value: 'instagram_followers', sortable: false, type: 'text', editable: true },
      { text: '#Followers', value: 'tiktok_followers', sortable: false, type: 'text', editable: true },
      { text: 'Edad', value: 'age', sortable: false, type: 'text', editable: false },
      { text: 'Fecha nacimiento', value: 'birth_date', sortable: false, type: 'date', editable: true },
      { text: 'Ciudad', value: 'city', sortable: false, type: 'autocomplete', editable: true, items: 'citiesListMap', itemText: 'name', itemValue: 'id' },

      { text: 'Story', value: 'story_price', sortable: false, type: 'currency', editable: true },
      { text: 'Set de stories 3 stories / haul (45-60 seg)', value: 'story_set_price', sortable: false, type: 'currency', editable: true },
      { text: 'POST', value: 'post_price', sortable: false, type: 'currency', editable: true },
      { text: 'Carrusel', value: 'carrousel_price', sortable: false, type: 'currency', editable: true },
      { text: 'Reel', value: 'reel_price', sortable: false, type: 'currency', editable: true },
      { text: 'Tiktok', value: 'tiktok_price', sortable: false, type: 'currency', editable: true },
      { text: 'Replicado', value: 'replicado_price', sortable: false, type: 'currency', editable: true },

      { text: 'Contrato', value: 'contract', sortable: false, type: 'file', editable: true },
      { text: 'Periodo de Prueba', value: 'probation', sortable: false, type: 'text', editable: true },
      { text: 'Tiempo de Vigencia', value: 'validity', sortable: false, type: 'text', editable: true },
      { text: 'Fecha de Inicio', value: 'entrance_date', sortable: false, type: 'text', editable: true },
      { text: 'Fecha de Conclusión', value: 'departure_date', sortable: false, type: 'text', editable: true },
      { text: 'Porcentaje de Contrato', value: 'commission_percentage', sortable: false, type: 'text', editable: true },
      { text: 'Renovacion', value: 'renewal', sortable: false, type: 'text', editable: true },
      { text: 'Periodos de prorroga', value: 'extension_period', sortable: false, type: 'text', editable: true },
      { text: 'Carta de Representación', value: 'cover_letter', sortable: false, type: 'file', editable: true },
      { text: 'Correo', value: 'email', sortable: false, type: 'text', editable: true },
      { text: 'Comentarios', value: 'comments', sortable: false, type: 'textarea', editable: true },

      { text: 'Dirección', value: 'address', sortable: false, width: 300, type: 'text', editable: true },
      { text: 'RFC o CURP', value: 'rfc', sortable: false, type: 'text', editable: true },
      { text: 'Acciones', value: 'actions', sortable: false, type: 'actions', editable: true },
    ],
    searchbyCategory: '',
    searchbyState: '',
    searchbyCity: ''
  }),
  computed: {
    currentMonth() {
      return new Date().toLocaleString("es-ES", { month: "long" });
    },
    currentUser() {
      return this.$store.state.currentUser.user;
    },
    statesList() {
      return this.$store.state.state.states;
    },
    categories() {
      return this.$store.state.category.categories;
    }
  },
  watch: {
    searchbyState(val) {
      if (val) this.getCitiesFromApi();
      else this.citiesList = [];
    },
    options: {
      handler: "getDataFromApi",
      deep: true,
    },
    search: "getDataFromApi",
  },
  mounted() {
    this.$store.dispatch('state/getStates');
    this.$store.dispatch('category/getCategories');
    this.$store.dispatch('influencer_regimen/getRegimenes');
  },
  methods: {
    onDatePicked(date) {
      this.editCell.value = this.formatDatePicker(date);
      this.datePickerMenu = false;
      this.saveCell(this.editCell.originalItem, this.headers.find(h => h.value === 'birth_date'));
    },
    formatDatePicker(date) {
      // De aaaa-mm-dd a dd-mm-aaaa (para mostrar)
      if (!date) return '';
      const [y, m, d] = date.split('-');
      return `${d}-${m}-${y}`;
    },
    parseDateForDb(date) {
      // De dd-mm-aaaa a aaaa-mm-dd (para guardar)
      if (!date) return '';
      const [d, m, y] = date.split('-');
      return `${y}-${m}-${d}`;
    },
    onFileChange(event, item, header) {
      let file = event;
      if (Array.isArray(file)) file = file[0];
      this.editCell.value = file;
      // Aquí SÍ llamas saveCell
      this.saveCell(item, header);
    },
    getTalentsAgentList(item) {
      if (!item) return [];
      if (item.is_booking) {
        return this.$store.state.user.users.filter(
          u => u.role === 'Booking' || u.role === 'Booking Manager'
        );
      } else {
        return this.$store.state.user.users.filter(
          u => u.role === 'Talent Agent' || u.role === 'Talent Agent Manager'
        );
      }
    },
    hasPermission(permission) {
      return this.currentUser.id === 1 || (this.currentUser.permissions?.includes(permission) || false);
    },
    calcularEdad(fecha) {
        if (!fecha) return "";
        let d, m, y;
        if (/^\d{4}-\d{2}-\d{2}$/.test(fecha)) {
            // Formato aaaa-mm-dd
            [y, m, d] = fecha.split('-');
        } else if (/^\d{2}-\d{2}-\d{4}$/.test(fecha)) {
            // Formato dd-mm-aaaa
            [d, m, y] = fecha.split('-');
        } else {
            return "";
        }
        const nacimiento = new Date(`${y}-${m}-${d}`);
        const hoy = new Date();
        let edad = hoy.getFullYear() - nacimiento.getFullYear();
        if (
            hoy.getMonth() < nacimiento.getMonth() ||
            (hoy.getMonth() === nacimiento.getMonth() && hoy.getDate() < nacimiento.getDate())
        ) {
            edad--;
        }
        return edad;
    },
    formatDate(fecha) {
      if (!fecha) return '';
      // formato aaaa-mm-dd => dd-mm-aaaa
      if (fecha.includes('-')) {
        const parts = fecha.split('-');
        if (parts[0].length === 4) return `${parts[2]}-${parts[1]}-${parts[0]}`;
      }
      return fecha;
    },
    existValue(item, field = 'name') {
      return item && item[field] ? item[field] : '';
    },
    buildFilterParams(extraParams = {}) {
      let filters = localStorage.getItem('filtersInfluencers') ? JSON.parse(localStorage.getItem('filtersInfluencers')) : '';
      let params = filters ? '&' + filters : '';
      if (this.search) params += `&filter[name]=${this.search}`;
      else if (!this.searchbyCategory && !this.searchbyState && !this.searchbyCity) params += '&filter[is_booking]=0';
      const { role, id } = this.currentUser;
      if (role === 'Talent Agent' || role === 'Booking') params += `&filter[parent_id]=${id}`;
      else if (role === 'Talent Agent Manager') params += '&filter[is_booking]=0';
      else if (role === 'Booking Manager') params += '&filter[is_booking]=1';
      if (this.searchbyCategory) params += `&filter[categories.id]=${this.searchbyCategory}`;
      if (this.searchbyState) params += `&filter[state]=${this.searchbyState}`;
      if (this.searchbyCity) params += `&filter[city]=${this.searchbyCity}`;
      Object.entries(extraParams).forEach(([k, v]) => {
        if (v) params += `&${k}=${v}`;
      });
      return params;
    },
    async getCitiesFromApi() {
      const url = `${process.env.VUE_APP_BACKEND_ROUTE}api/v1/cities?state_id=${this.searchbyState}`;
      const { data } = await axios.get(url);
      this.citiesList = data;
    },
    async getDataFromApi() {
      this.loading = true;
      try {
        const { sortBy, sortDesc, page, itemsPerPage } = this.options;
        let params = this.buildFilterParams();
        let url = `${process.env.VUE_APP_BACKEND_ROUTE}api/v1/influencers?page=${page}&itemsPerPage=${itemsPerPage}${params}`;
        if (sortBy?.length === 1) {
          const sort = sortDesc[0] ? `-${sortBy[0]}` : sortBy[0];
          url += `&sort=${sort}`;
        }
        const { data } = await axios.get(url);
        this.influencers = this.mapInfluencers(data.data);
        this.totalInfluencers = data.meta.total;
      } finally {
        this.loading = false;
      }
    },
    async exportExcel(itemsPerPage) {
      const params = this.buildFilterParams({ itemsPerPage, export: true });
      const config = {
        responseType: 'blob',
        headers: {
          Authorization: AUTH_HEADER,
          Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }
      };
      const url = `https://unopack.mx/api/v1/influencer/export?itemsPerPage=${itemsPerPage}&export=true${params}`;
      const response = await axios.get(url, config);
      this.downloadFile(response.data, 'influencers.xlsx');
    },
    async downloadPDF(influencer) {
      const config = {
        responseType: 'blob',
        headers: {
          Authorization: AUTH_HEADER,
          Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }
      };
      const url = 'https://unopack.mx/api/v1/influencer/tax_collabs_pdf';
      const { data } = await axios.post(url, { influencer_id: influencer.id }, config);
      const filename = `Facturas ${this.currentMonth} - ${influencer.name}.pdf`;
      this.downloadFile(data, filename);
    },
    downloadFile(blobData, filename) {
      const url = window.URL.createObjectURL(new Blob([blobData]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    },
    mapInfluencers(list) {
      return list.map(i => ({
        id: i.id,
        email: i.email,
        name: `${i.name} ${i.last}`,
        phone: i.phone,
        photo: i.photo,
        profile_photo_path: i.profile_photo_path,
        commission_percentage: (i.commission_percentage * 1) + '%',
        is_booking: i.is_booking,
        location: [this.existValue(i.city), this.existValue(i.state)].filter(Boolean).join(', '),
        categories: i.categories,
        birth_date: this.formatDate(i.birth_date),
        age: this.calcularEdad(this.formatDate(i.birth_date)),
        address: i.address,
        department: i.department,
        tax_regime: i.tax_regime,
        instagram: i.social_networks?.instagram || '',
        tiktok: i.social_networks?.tiktok || '',
        instagram_followers: i.instagram_followers,
        tiktok_followers: i.tiktok_followers,
        agent: i.agent?.name,
        parent_id: i.agent?.id,
        city: i.city?.name,
        city_id: i.city?.id,
        state_id: i.state?.id || i.state_id,
        editedItem: [i].map(id=>{
            return{
                department:id.department,
                id:id.id,
                parent_id:id.agent?.id,
                name:id.name,
                last:id.last,
                email:id.email,
                phone:id.phone,
                status:id.status,
                state:id.state?.id,
                city:id.city?.id,
                is_booking:id.is_booking,
                social_networks:id.social_networks,
                bank_account_details:id.bank_account_details,
                commission_percentage:id.commission_percentage,
                role:'Influencer',
                category_ids:id.categories.map(id=>id.id),
                birth_date:id.birth_date,
                address:id.address,
                tax_regime_id:id.tax_regime!=null?id.tax_regime.id:null,
                instagram_followers:id.instagram_followers,
                tiktok_followers:id.tiktok_followers,
                rfc:id.rfc,
                cdf:id.cdf,
                ine:id.ine,
                pasaporte:id.pasaporte,
                visa:id.visa,
                comprobante_domicilio:id.comprobante_domicilio,
                tallas:id.tallas==null?[{label:'', value:''}]:id.tallas
            }
        })[0],
        story_price: i.story_price,
        story_set_price: i.story_set_price,
        post_price: i.post_price,
        carrousel_price: i.carrousel_price,
        reel_price: i.reel_price,
        tiktok_price: i.tiktok_price,
        replicado_price: i.replicado_price,
        contract: i.contract,
        probation: i.probation,
        validity: i.validity,
        entrance_date: i.entrance_date,
        departure_date: i.departure_date,
        commission_percentage: i.commission_percentage,
        renewal: i.renewal,
        extension_period: i.extension_period,
        cover_letter: i.cover_letter,
        comments: i.comments,
        rfc: i.rfc,
        social_networks: i.social_networks || {},
      }));
    },
    async deleteItem(item) {
      if (confirm('¿Seguro que deseas eliminar esta influencer?')) {
        await axios.delete(`${process.env.VUE_APP_BACKEND_ROUTE}api/v1/users/${item.id}`);
        this.getDataFromApi();
        this.snackbar = {
          color: 'success',
          show: true,
          message: 'Influencer eliminado(a) con éxito',
        }
      }
    },
    edit(item) {
      this.editedItem = item;
      this.editDialog = true;
    },
    closeDialogItem(val) {
      this.editDialog = val;
      this.createDialog = val;
      this.getDataFromApi();
    },
    async startEditCell(item, header) {
        if (!header.editable) return;

        if (header.type === 'file') {
            this.editCell.value = null;
        } else if (header.value === 'city') {
            this.editCell.value = item.city_id;

            // 1. OBTÉN EL STATE_ID (de la fila)
            let stateId = item.state_id;

            // 2. SI EXISTE STATE_ID, HAZ EL GET DE CIUDADES POR ESTADO
            if (stateId) {
            const { data } = await axios.get(`${process.env.VUE_APP_BACKEND_ROUTE}api/v1/cities?state_id=${stateId}`);
            this.$set(this.citiesListMap, item.id, data);
            } else {
            // Si no tienes stateId, trae todas las ciudades
            const { data } = await axios.get(`${process.env.VUE_APP_BACKEND_ROUTE}api/v1/cities`);
            this.$set(this.citiesListMap, item.id, data);
            }
        } else if (header.value === 'agent') {
            this.editCell.value = item.parent_id;
        } else if (header.value === 'instagram' || header.value === 'tiktok') {
            this.editCell.value = item[header.value];
        } else if (header.value === 'birth_date') {
            this.editCell.value = item.birth_date;
            this.datePicker = item.birth_date ? item.birth_date.split('-').reverse().join('-') : null;
        } else {
            this.editCell.value = item[header.value];
        }

        this.editCell = {
            id: item.id,
            field: header.value,
            value: this.editCell.value,
            type: header.type,
            originalItem: item,
        };
        },
    getInputComponent(type) {
      if (type === 'textarea') return 'VTextarea';
      if (type === 'file') return 'VFileInput';
      return 'v-text-field';
    },
    async saveCell(item, header) {
      if (this.editCell.value === item[header.value]) {
        this.editCell = { id: null, field: null, value: null, type: null };
        return;
      }
      try {
        let valueToSave = this.editCell.value;
        let patchData = {};

        // -------- AGENTE --------
        if (header.value === 'agent') {
          patchData['parent_id'] = valueToSave;
        }
        // -------- SOCIAL NETWORKS --------
        else if (header.value === 'instagram' || header.value === 'tiktok') {
          let updatedNetworks = { ...(item.social_networks || {}) };
          updatedNetworks[header.value] = valueToSave;
          patchData['social_networks'] = updatedNetworks;
        }
        // -------- CITY --------
        else if (header.value === 'city') {
          patchData['city_id'] = valueToSave;
        }
        // -------- FECHA DE NACIMIENTO --------
        else if (header.value === 'birth_date') {
          // De dd-mm-aaaa => aaaa-mm-dd
          patchData['birth_date'] = this.parseDateForDb(valueToSave);
        }
        // -------- FILE --------
        else if (header.type === 'file' && (valueToSave instanceof File || (Array.isArray(valueToSave) && valueToSave[0] instanceof File))) {
          if (Array.isArray(valueToSave)) valueToSave = valueToSave[0];
          const formData = new FormData();
          formData.append('file', valueToSave);
          formData.append('field', header.value);
          formData.append('influencer_id', item.id);
          const uploadResp = await axios.post(
            process.env.VUE_APP_BACKEND_ROUTE + "api/v1/sale/file",
            formData,
            { headers: { 'Content-Type': 'multipart/form-data' } }
          );
          patchData[header.value] = uploadResp.data.file;
        }
        // -------- DEFAULT --------
        else {
          patchData[header.value] = valueToSave;
        }

        // PATCH con el valor final
        await axios.patch(
          process.env.VUE_APP_BACKEND_ROUTE + "api/v1/influencers/" + item.id,
          patchData
        );

        // Actualiza en UI
        Object.keys(patchData).forEach(k => {
          item[k] = patchData[k];
        });
        if (patchData['city_id']) item.city = (this.citiesListMap[item.id] || []).find(c => c.id === patchData['city_id'])?.name || '';
        if (patchData['parent_id']) item.agent = (this.getTalentsAgentList(item).find(a => a.id === patchData['parent_id']) || {}).name || '';
        if (patchData['social_networks']) {
          item.instagram = patchData.social_networks.instagram;
          item.tiktok = patchData.social_networks.tiktok;
        }
        if (patchData['birth_date']) item.birth_date = this.formatDate(patchData['birth_date']);

        this.snackbar = {
          color: 'success',
          show: true,
          message: `Campo ${header.text} modificado con éxito`,
        };
      } catch (e) {
        this.snackbar = {
          color: 'error',
          show: true,
          message: `No se pudo modificar el campo ${header.text}`,
        };
      }
      this.editCell = { id: null, field: null, value: null, type: null };
    },
    formatCell(value, type) {
      if (type === 'currency') {
        return value ? '$' + Number(value).toLocaleString() : '$0';
      }
      return value;
    },
  }
};
</script>

<style scoped>
.btnText {
  padding: 0 !important;
  height: auto !important;
  background: white !important;
  box-shadow: none !important;
  font-weight: 400;
  font-family: "Roboto", sans-serif;
  letter-spacing: 0;
}
.editable-cell .edit-icon {
  opacity: 0;
  transition: opacity 0.15s;
  pointer-events: none;
}
.editable-cell:hover .edit-icon {
  opacity: 1;
  pointer-events: auto;
}
.name-cell {
  display: flex;
  align-items: center;
}
</style>
